import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { projectsAPI, offersAPI, customersAPI, invoicesAPI, legacyOffersAPI, api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import ProjectStatusLamps from "../components/ProjectStatusLamps";
import {
  ArrowLeft,
  FileText,
  Mail,
  Send,
  CheckCircle,
  FileCheck,
  Loader2,
  User,
  MapPin,
  Calendar,
  Clock,
  Package,
  Euro,
  ClipboardList,
  Eye,
  Receipt,
  Plus,
  XCircle,
  AlertTriangle,
  Archive,
  EyeOff,
  RefreshCw,
  Link as LinkIcon
} from "lucide-react";

const INCOGNITO_EMAIL = "info@sunprojects-gmbh.de";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [signedOffer, setSignedOffer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingOffer, setSendingOffer] = useState(false);
  const [sendingContract, setSendingContract] = useState(false);
  const [generatingContract, setGeneratingContract] = useState(false);
  const [generatingConfirmation, setGeneratingConfirmation] = useState(false);
  const [sendingConfirmation, setSendingConfirmation] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [finalizingInvoice, setFinalizingInvoice] = useState(null);
  const [markingAsPaid, setMarkingAsPaid] = useState(null);
  const [markingOfferAccepted, setMarkingOfferAccepted] = useState(false);
  
  // Dialog for marking offer as accepted
  const [showMarkAcceptedDialog, setShowMarkAcceptedDialog] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState("");
  const [projectOffers, setProjectOffers] = useState([]);
  
  // Legacy offer assignment
  const [showLegacyDialog, setShowLegacyDialog] = useState(false);
  const [customerLegacyOffers, setCustomerLegacyOffers] = useState([]);
  const [selectedLegacyOfferId, setSelectedLegacyOfferId] = useState("");
  const [assigningLegacy, setAssigningLegacy] = useState(false);
  const [linkedLegacyOffer, setLinkedLegacyOffer] = useState(null);
  
  // Invoice creation dialog
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [newInvoiceType, setNewInvoiceType] = useState("abschlagsrechnung");
  const [newInvoicePartial, setNewInvoicePartial] = useState("1");
  const [newInvoiceCustomPct, setNewInvoiceCustomPct] = useState("60");
  // MwSt-Ausweis für Non-PV-Angebote (Freitext/WP/Legacy). Default: aus CI (undefined = kein Override)
  const [newInvoiceIncludeVat, setNewInvoiceIncludeVat] = useState(true);
  // „Nullbetrag"-Schlussrechnung (§35a EStG Lohnkostennachweis)
  const [newInvoiceForceZeroBalance, setNewInvoiceForceZeroBalance] = useState(false);
  const [newInvoiceNotes, setNewInvoiceNotes] = useState("");

  // Auftragsbestätigung: PDF-Ausweisung Einzelpreise (Default = true, übernimmt sich vom Angebot)
  const [abShowItemPrices, setAbShowItemPrices] = useState(true);

  // Anlagentyp & Meldungs-Felder (Einspeiseanfrage / MaStR / Fertigmeldung / §14a)
  const [registrations, setRegistrations] = useState({
    system_type: "",
    einspeiseanfrage_number: "", einspeiseanfrage_date: "",
    marktstammdaten_number: "", marktstammdaten_date: "",
    fertigmeldung_number: "", fertigmeldung_date: "",
    paragraph14a_number: "", paragraph14a_date: "",
  });
  const [savingRegistrations, setSavingRegistrations] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load project
      const projectRes = await projectsAPI.get(id);
      setProject(projectRes.data);
      // Sync registrations state from project
      const p = projectRes.data;
      setRegistrations({
        system_type: p.system_type || "",
        einspeiseanfrage_number: p.einspeiseanfrage_number || "",
        einspeiseanfrage_date: p.einspeiseanfrage_date || "",
        marktstammdaten_number: p.marktstammdaten_number || "",
        marktstammdaten_date: p.marktstammdaten_date || "",
        fertigmeldung_number: p.fertigmeldung_number || "",
        fertigmeldung_date: p.fertigmeldung_date || "",
        paragraph14a_number: p.paragraph14a_number || "",
        paragraph14a_date: p.paragraph14a_date || "",
      });
      
      // Load customer
      if (projectRes.data.customer_id) {
        const customerRes = await customersAPI.get(projectRes.data.customer_id);
        setCustomer(customerRes.data);
      }
      
      // Load signed offer for this project (Native PV)
      const offersRes = await offersAPI.list(id);
      const allOffers = [...(offersRes.data || [])];

      // Also pull free/wp/legacy offers for the same customer (they may live without a project_id link)
      const customerId = projectRes.data.customer_id;
      if (customerId) {
        try {
          const [freeRes, wpRes, legacyRes] = await Promise.all([
            api.get(`/free-offers/?customer_id=${customerId}`).catch(() => ({ data: [] })),
            api.get(`/wp-offers/?customer_id=${customerId}`).catch(() => ({ data: [] })),
            api.get(`/legacy-offers/?customer_id=${customerId}`).catch(() => ({ data: [] })),
          ]);
          // Free / WP / Legacy: nur, wenn zum Projekt verknüpft ODER project_id leer (orphan – gleicher Kunde)
          const includeOrphan = (o) =>
            o && (o.project_id === id || !o.project_id);
          const unwrap = (r) => {
            const d = r?.data;
            if (Array.isArray(d)) return d;
            if (Array.isArray(d?.items)) return d.items;
            if (Array.isArray(d?.data)) return d.data;
            return [];
          };
          const tagged = (arr, t) => arr.filter(includeOrphan).map(o => ({ ...o, _doc_type: t }));
          allOffers.push(...tagged(unwrap(freeRes), "free_offer"));
          allOffers.push(...tagged(unwrap(wpRes), "wp_offer"));
          allOffers.push(...tagged(unwrap(legacyRes), "legacy_offer"));
        } catch (e) {
          console.log("Could not load other offer types:", e);
        }
      }

      setProjectOffers(allOffers);

      // Find the "active" offer - either signed OR accepted via accepted_offer_id
      let activeOffer = allOffers.find(o => o.signature);

      // If no signed offer but project has accepted_offer_id, use that
      if (!activeOffer && projectRes.data.accepted_offer_id) {
        activeOffer = allOffers.find(o => o.id === projectRes.data.accepted_offer_id);
      }

      // Also check for offers with status "angenommen"
      if (!activeOffer) {
        activeOffer = allOffers.find(o => o.status === "angenommen");
      }
      
      setSignedOffer(activeOffer || null);
      if (activeOffer && typeof activeOffer.show_item_prices === "boolean") {
        setAbShowItemPrices(activeOffer.show_item_prices);
      }
      
      // Load invoices for this project (via offer)
      if (activeOffer) {
        try {
          const invoicesRes = await invoicesAPI.list({ offer_id: activeOffer.id });
          setInvoices(invoicesRes.data || []);
        } catch (err) {
          console.log("No invoices found or error:", err);
          setInvoices([]);
        }
      }
      
      // Load linked legacy offer if project has legacy_offer_id
      if (projectRes.data.legacy_offer_id) {
        try {
          const legacyRes = await legacyOffersAPI.get(projectRes.data.legacy_offer_id);
          setLinkedLegacyOffer(legacyRes.data);
        } catch (err) {
          console.log("Legacy offer not found:", err);
        }
      }
      
      // Also load invoices from legacy offer
      if (projectRes.data.legacy_offer_id) {
        try {
          const legacyInvoicesRes = await invoicesAPI.list({ legacy_offer_id: projectRes.data.legacy_offer_id });
          if (legacyInvoicesRes.data && legacyInvoicesRes.data.length > 0) {
            setInvoices(prev => [...prev, ...legacyInvoicesRes.data]);
          }
        } catch (err) {
          console.log("No legacy invoices found:", err);
        }
      }
      
    } catch (error) {
      console.error("Failed to load project:", error);
      toast.error("Projekt konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  // Load legacy offers for customer when dialog opens
  const loadCustomerLegacyOffers = async () => {
    if (!customer?.id) return;
    try {
      const response = await legacyOffersAPI.list({ customer_id: customer.id });
      // Filter out already linked legacy offers
      const available = (response.data.data || []).filter(lo => !lo.project_id || lo.project_id === id);
      setCustomerLegacyOffers(available);
    } catch (error) {
      console.error("Failed to load legacy offers:", error);
      toast.error("Legacy-Angebote konnten nicht geladen werden");
    }
  };

  const handleAssignLegacyOffer = async () => {
    if (!selectedLegacyOfferId) {
      toast.error("Bitte wählen Sie ein Legacy-Angebot aus");
      return;
    }
    
    try {
      setAssigningLegacy(true);
      
      // Use the dedicated API endpoint to link legacy offer to project
      await legacyOffersAPI.linkToProject(id, selectedLegacyOfferId);
      
      toast.success("Legacy-Angebot erfolgreich verknüpft");
      setShowLegacyDialog(false);
      setSelectedLegacyOfferId("");
      loadData();
    } catch (error) {
      console.error("Failed to assign legacy offer:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Verknüpfen");
    } finally {
      setAssigningLegacy(false);
    }
  };

  const handleSendOffer = async () => {
    if (!signedOffer) return;
    setSendingOffer(true);
    try {
      const response = await offersAPI.sendOffer(signedOffer.id);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      console.error("Failed to send offer:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Versenden des Angebots");
    } finally {
      setSendingOffer(false);
    }
  };

  const handleSendContract = async () => {
    if (!signedOffer) return;
    setSendingContract(true);
    try {
      const response = await offersAPI.sendContract(signedOffer.id);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      console.error("Failed to send contract:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Versenden des Kaufvertrags");
    } finally {
      setSendingContract(false);
    }
  };

  const handleGenerateContract = async () => {
    if (!signedOffer) return;
    setGeneratingContract(true);
    try {
      // Generate contract without seller signature (can be added later)
      const response = await offersAPI.generateContract(signedOffer.id, null);
      toast.success(response.data.message || "Kaufvertrag wurde erzeugt");
      loadData();
    } catch (error) {
      console.error("Failed to generate contract:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Erzeugen des Kaufvertrags");
    } finally {
      setGeneratingContract(false);
    }
  };

  const handleGenerateOrderConfirmation = async (showItemPricesOverride) => {
    setGeneratingConfirmation(true);
    try {
      const sip = (typeof showItemPricesOverride === "boolean") ? showItemPricesOverride : abShowItemPrices;
      const response = await projectsAPI.generateOrderConfirmation(id, sip);
      toast.success(response.data.message);
      await loadData(); // Await to ensure UI updates after data reload
    } catch (error) {
      console.error("Failed to generate order confirmation:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen der Auftragsbestätigung");
    } finally {
      setGeneratingConfirmation(false);
    }
  };

  const handleSendOrderConfirmation = async () => {
    setSendingConfirmation(true);
    try {
      const response = await projectsAPI.sendOrderConfirmation(id);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      console.error("Failed to send order confirmation:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Versenden der Auftragsbestätigung");
    } finally {
      setSendingConfirmation(false);
    }
  };

  // Invoice handlers
  const handleSaveRegistrations = async () => {
    setSavingRegistrations(true);
    try {
      // Backend wandelt leere Strings auto in null (= „nicht erledigt")
      const payload = { ...registrations };
      const updated = await projectsAPI.updateRegistrations(id, payload);
      setProject(updated.data);
      toast.success("Anlagentyp & Meldungen gespeichert");
    } catch (error) {
      console.error("Failed to save registrations:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern der Meldungen");
    } finally {
      setSavingRegistrations(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!signedOffer) return;
    // Client-side guard: Prozentsatz-Range 0<pct<=100
    if (newInvoiceType === "abschlagsrechnung" || newInvoiceType === "schlussrechnung") {
      const pct = parseFloat(newInvoiceCustomPct);
      if (isNaN(pct) || pct <= 0 || pct > 100) {
        toast.error("Prozentsatz muss zwischen 0,01 und 100 liegen");
        return;
      }
    }
    // Abschlags-Summenprüfung nur bei Abschlag
    if (newInvoiceType === "abschlagsrechnung") {
      const pct = parseFloat(newInvoiceCustomPct);
      const otherSum = invoices
        .filter(i => i.invoice_type === "abschlagsrechnung" && i.status !== "storno" && i.partial_number !== parseInt(newInvoicePartial))
        .reduce((s, i) => s + (parseFloat(i.partial_percentage) || 0), 0);
      if (otherSum + pct > 100.0001) {
        toast.error(`Summe aller Abschlagsprozente (${(otherSum + pct).toFixed(2)}%) übersteigt 100%`);
        return;
      }
    }
    setCreatingInvoice(true);
    try {
      const response = await invoicesAPI.create({
        offer_id: signedOffer.id,
        invoice_type: newInvoiceType,
        partial_number: newInvoiceType === "abschlagsrechnung" ? parseInt(newInvoicePartial) : null,
        custom_percentage: (newInvoiceType === "abschlagsrechnung" || newInvoiceType === "schlussrechnung") ? parseFloat(newInvoiceCustomPct) : null,
        include_vat_override: signedOffer && signedOffer._doc_type && signedOffer._doc_type !== "offer" ? newInvoiceIncludeVat : null,
        force_zero_balance: newInvoiceType === "schlussrechnung" ? !!newInvoiceForceZeroBalance : false,
        notes: newInvoiceNotes || null
      });
      toast.success("Rechnung erfolgreich erstellt (Entwurf)");
      setShowInvoiceDialog(false);
      setNewInvoiceNotes("");
      setNewInvoiceForceZeroBalance(false);
      loadData();
    } catch (error) {
      console.error("Failed to create invoice:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen der Rechnung");
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleFinalizeInvoice = async (invoice) => {
    if (!window.confirm(`Möchten Sie die Rechnung wirklich finalisieren? Nach der Finalisierung kann sie nicht mehr bearbeitet werden.`)) {
      return;
    }
    setFinalizingInvoice(invoice.id);
    try {
      const response = await invoicesAPI.finalize(invoice.id);
      toast.success(`Rechnung ${response.data.invoice_number} erfolgreich finalisiert`);
      loadData();
    } catch (error) {
      console.error("Failed to finalize invoice:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Finalisieren der Rechnung");
    } finally {
      setFinalizingInvoice(null);
    }
  };

  const handleSendInvoice = async (invoice) => {
    if (invoice.status !== "final") {
      toast.error("Nur finalisierte Rechnungen können versendet werden");
      return;
    }
    if (invoice.sent_at) {
      if (!window.confirm(`Diese Rechnung wurde bereits versendet. Möchten Sie sie erneut senden?`)) return;
    }
    setSendingInvoice(invoice.id);
    try {
      const response = await invoicesAPI.sendEmail(invoice.id);
      toast.success(response.data.message || "Rechnung erfolgreich versendet");
      loadData();
    } catch (error) {
      console.error("Failed to send invoice:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Versenden der Rechnung");
    } finally {
      setSendingInvoice(null);
    }
  };

  const handlePreviewInvoice = (invoice) => {
    if (invoice.status === "entwurf") {
      const url = invoicesAPI.getPreviewPdfUrl(invoice.id);
      window.open(url, '_blank');
    } else {
      const url = invoicesAPI.getPdfUrl(invoice.id);
      window.open(url, '_blank');
    }
  };

  const handleMarkAsPaid = async (invoice) => {
    if (invoice.payment_status === "paid") {
      toast.error("Rechnung ist bereits als bezahlt markiert");
      return;
    }
    if (!window.confirm(`Möchten Sie die Rechnung ${invoice.invoice_number} als bezahlt markieren?`)) {
      return;
    }
    setMarkingAsPaid(invoice.id);
    try {
      const response = await invoicesAPI.markAsPaid(invoice.id, {});
      toast.success(response.data.message || "Rechnung als bezahlt markiert");
      loadData();
    } catch (error) {
      console.error("Failed to mark invoice as paid:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Markieren als bezahlt");
    } finally {
      setMarkingAsPaid(null);
    }
  };

  const getInvoiceStatusBadge = (invoice) => {
    const statusConfig = {
      entwurf: { label: "Entwurf", color: "bg-amber-100 text-amber-800" },
      final: { label: "Finalisiert", color: "bg-green-100 text-green-800" },
      storno: { label: "Storniert", color: "bg-red-100 text-red-800" }
    };
    const config = statusConfig[invoice.status] || { label: invoice.status, color: "bg-gray-100 text-gray-800" };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getPaymentStatusBadge = (invoice) => {
    if (invoice.status !== "final" || invoice.invoice_type === "stornorechnung") {
      return null;
    }
    const statusConfig = {
      pending: { label: "Zahlung erwartet", color: "bg-orange-100 text-orange-800 border-orange-200" },
      paid: { label: "Bezahlt", color: "bg-green-100 text-green-800 border-green-200" },
      overdue: { label: "Überfällig", color: "bg-red-100 text-red-800 border-red-200" }
    };
    const config = statusConfig[invoice.payment_status] || statusConfig.pending;
    return <Badge variant="outline" className={config.color}>{config.label}</Badge>;
  };

  const getInvoiceTypeName = (type) => {
    const types = {
      abschlagsrechnung: "Abschlagsrechnung",
      schlussrechnung: "Schlussrechnung",
      stornorechnung: "Stornorechnung"
    };
    return types[type] || type;
  };

  // Calculate which partial numbers are already used
  const getUsedPartialNumbers = () => {
    return invoices
      .filter(i => i.invoice_type === "abschlagsrechnung" && i.status !== "storno")
      .map(i => i.partial_number);
  };

  const getNextPartialNumber = () => {
    const used = getUsedPartialNumbers();
    for (let i = 1; i <= 2; i++) {  // Only 2 partial invoices for standard (60% + 30%)
      if (!used.includes(i)) return i.toString();
    }
    return "1";
  };

  // Determine available invoice options based on payment terms and existing invoices
  const getAvailableInvoiceOptions = () => {
    // First check: Order confirmation must exist before any invoice can be created
    if (!project?.order_confirmation_generated_at) {
      return {
        canCreateAbschlag: false,
        availablePartials: [],
        canCreateSchluss: false,
        message: "Bitte erstellen Sie zuerst eine Auftragsbestätigung, bevor Sie Rechnungen erstellen können.",
        requiresOrderConfirmation: true
      };
    }
    
    const paymentTerms = signedOffer?.payment_terms || "standard";
    const usedPartials = getUsedPartialNumbers();
    const hasSchlussrechnung = invoices.some(i => i.invoice_type === "schlussrechnung" && i.status !== "storno");
    
    if (paymentTerms === "prepayment") {
      // Vorkasse: Only one invoice (100%) allowed
      const hasInvoice = invoices.some(i => i.status !== "storno");
      return {
        canCreateAbschlag: !hasInvoice,
        availablePartials: hasInvoice ? [] : [1],
        canCreateSchluss: false,
        message: hasInvoice ? "Bei Vorkasse ist nur eine Rechnung möglich" : null
      };
    }
    
    // Standard: 60% / 30% / 10%
    // Abschlag 1 (60%) → Abschlag 2 (30%) → Schlussrechnung (10%)
    const hasAbschlag1 = usedPartials.includes(1);
    const hasAbschlag2 = usedPartials.includes(2);
    
    // Available partials that haven't been created yet
    const availablePartials = [];
    if (!hasAbschlag1) availablePartials.push(1);
    if (hasAbschlag1 && !hasAbschlag2) availablePartials.push(2);
    
    // Schlussrechnung: immer wählbar. Wenn schon eine existiert, wird die neue Rechnung
    // automatisch als „Schlussauflistung" (Nullbetrag) markiert.
    const canCreateSchluss = true;
    const isSchlussauflistung = hasSchlussrechnung;

    let message = null;
    if (hasSchlussrechnung && availablePartials.length === 0) {
      message = "Sie können zusätzlich eine Schlussauflistung mit 0 € erzeugen (dokumentiert alle Zahlungen inkl. §35a EStG-Nachweis).";
    }
    
    return {
      canCreateAbschlag: availablePartials.length > 0,
      availablePartials,
      canCreateSchluss,
      isSchlussauflistung,
      message
    };
  };

  const invoiceOptions = signedOffer ? getAvailableInvoiceOptions() : null;

  // Preview functions - open PDFs in new tab
  const handlePreviewOffer = () => {
    if (signedOffer) {
      const url = offersAPI.getPdfUrl(signedOffer.id);
      window.open(url, '_blank');
    }
  };

  const handlePreviewContract = () => {
    if (signedOffer?.contract_generated_at) {
      const url = offersAPI.getContractPdfUrl(signedOffer.id);
      window.open(url, '_blank');
    }
  };

  const handlePreviewOrderConfirmation = () => {
    if (project?.order_confirmation_generated_at) {
      const url = projectsAPI.getOrderConfirmationPdfUrl(id, abShowItemPrices);
      window.open(url, '_blank');
    }
  };

  // Open dialog to select offer and mark project as "Angebot angenommen"
  const openMarkAcceptedDialog = () => {
    // Pre-select the signed offer if available, otherwise the first offer
    if (signedOffer) {
      setSelectedOfferId(signedOffer.id);
    } else if (projectOffers.length > 0) {
      setSelectedOfferId(projectOffers[0].id);
    } else {
      setSelectedOfferId("");
    }
    setShowMarkAcceptedDialog(true);
  };

  // Confirm and mark project as "Angebot angenommen" with selected offer
  const handleConfirmMarkOfferAccepted = async () => {
    if (!selectedOfferId) {
      toast.error("Bitte wählen Sie ein Angebot aus");
      return;
    }
    
    setMarkingOfferAccepted(true);
    try {
      await projectsAPI.markOfferAccepted(id, selectedOfferId);
      toast.success("Projekt wurde als 'Angebot angenommen' markiert");
      setShowMarkAcceptedDialog(false);
      loadData(); // Reload to get updated status
    } catch (error) {
      console.error("Error marking offer as accepted:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Markieren des Projekts");
    } finally {
      setMarkingOfferAccepted(false);
    }
  };

  // Toggle incognito mode (only Sunprojects admin)
  const isIncognitoAdmin = (user?.email || "").trim().toLowerCase() === INCOGNITO_EMAIL;
  const [togglingIncognito, setTogglingIncognito] = useState(false);
  const handleToggleIncognito = async () => {
    if (!project) return;
    const next = !project.is_incognito;
    if (next && !window.confirm("Projekt in den Inkognito-Modus versetzen?\n\nAlle verbundenen Angebote, Rechnungen und Signaturen werden für alle anderen Nutzer unsichtbar.")) {
      return;
    }
    setTogglingIncognito(true);
    try {
      await api.patch(`/projects/${id}/incognito`, { is_incognito: next });
      toast.success(next ? "Inkognito-Modus aktiviert" : "Inkognito-Modus deaktiviert");
      loadData();
    } catch (error) {
      console.error("Toggle incognito failed:", error);
      toast.error("Inkognito-Toggle fehlgeschlagen", { description: error?.response?.data?.detail || error.message });
    } finally {
      setTogglingIncognito(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      erstellt: { label: "Erstellt", variant: "secondary" },
      angebot_erstellt: { label: "Angebot erstellt", variant: "outline" },
      angebot_angenommen: { label: "Angebot angenommen", variant: "default" },
      in_planung: { label: "In Planung", variant: "default" },
      installation: { label: "Installation", variant: "default" },
      abgeschlossen: { label: "Abgeschlossen", variant: "success" }
    };
    const config = statusConfig[status] || { label: status, variant: "secondary" };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR"
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Projekt nicht gefunden</p>
        <Button variant="link" onClick={() => navigate("/projekte")}>
          Zurück zur Übersicht
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/projekte")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold font-['Manrope']">
                Projekt {project.project_number}
              </h1>
              <p className="text-muted-foreground">
                {customer ? `${customer.first_name} ${customer.last_name}` : "Kein Kunde"}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {project.is_incognito && (
            <Badge
              variant="outline"
              className="border-purple-500 bg-purple-50 text-purple-800 gap-1 font-semibold"
              data-testid="incognito-badge"
            >
              <EyeOff className="h-3 w-3" />
              Inkognito
            </Badge>
          )}
          {isIncognitoAdmin && (
            <Button
              variant={project.is_incognito ? "default" : "outline"}
              size="sm"
              className={project.is_incognito ? "bg-purple-600 hover:bg-purple-700 text-white" : "border-purple-400 text-purple-700 hover:bg-purple-50"}
              onClick={handleToggleIncognito}
              disabled={togglingIncognito}
              data-testid="toggle-incognito-btn"
              title="Inkognito-Modus umschalten"
            >
              {togglingIncognito ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <EyeOff className="h-4 w-4" />
              )}
              <span className="ml-1.5 hidden sm:inline">
                {project.is_incognito ? "Inkognito AN" : "Inkognito"}
              </span>
            </Button>
          )}
          {getStatusBadge(project.status)}
        </div>
      </div>

      {/* Status-Lämpchen für Anlagen-Meldungen (erscheinen nach AB-Versand) */}
      {project.order_confirmation_sent_at && (
        <div className="flex items-center gap-3 -mt-3 ml-12 text-sm" data-testid="project-detail-lamps-header">
          <span className="text-muted-foreground">Meldungen:</span>
          <ProjectStatusLamps project={project} size="large" />
        </div>
      )}

      {/* Alert for manual status update - show when project is not yet "angebot_angenommen" but has a signed offer or user wants to manually mark */}
      {project.status !== "angebot_angenommen" && 
       project.status !== "in_planung" && 
       project.status !== "installation" && 
       project.status !== "abgeschlossen" && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Projektstatus aktualisieren</AlertTitle>
          <AlertDescription className="text-amber-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-2">
              <p className="text-sm">
                {signedOffer 
                  ? "Das Angebot wurde unterschrieben, aber der Projektstatus wurde nicht automatisch aktualisiert."
                  : "Markieren Sie das Projekt manuell als 'Angebot angenommen', um mit Kaufvertrag, Auftragsbestätigung und Rechnungen fortzufahren."
                }
              </p>
              <Button
                onClick={openMarkAcceptedDialog}
                disabled={markingOfferAccepted || projectOffers.length === 0}
                variant="outline"
                className="shrink-0 border-amber-300 bg-white hover:bg-amber-100 text-amber-800"
                data-testid="mark-offer-accepted-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Angebot als angenommen markieren
              </Button>
            </div>
            {projectOffers.length === 0 && (
              <p className="text-xs text-amber-600 mt-2">
                Hinweis: Erstellen Sie zuerst ein Angebot für dieses Projekt.
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Alert for assigning an offer when project is already "angebot_angenommen" but no offer is linked */}
      {project.status === "angebot_angenommen" && !project.accepted_offer_id && projectOffers.length > 0 && (
        <Alert className="border-blue-200 bg-blue-50">
          <FileText className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-800">Angebot zuweisen</AlertTitle>
          <AlertDescription className="text-blue-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-2">
              <p className="text-sm">
                Das Projekt ist als "Angebot angenommen" markiert, aber es wurde noch kein Angebot zugewiesen. 
                Bitte wählen Sie das angenommene Angebot aus.
              </p>
              <Button
                onClick={openMarkAcceptedDialog}
                disabled={markingOfferAccepted}
                variant="outline"
                className="shrink-0 border-blue-300 bg-white hover:bg-blue-100 text-blue-800"
                data-testid="assign-offer-btn"
              >
                <FileText className="h-4 w-4 mr-2" />
                Angebot zuweisen
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Show linked legacy offer info */}
      {linkedLegacyOffer && (
        <Alert className="border-purple-200 bg-purple-50">
          <Archive className="h-4 w-4 text-purple-600" />
          <AlertTitle className="text-purple-800">Verknüpftes Legacy-Angebot</AlertTitle>
          <AlertDescription className="text-purple-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-2">
              <div className="text-sm">
                <span className="font-medium">{linkedLegacyOffer.title}</span>
                {linkedLegacyOffer.offer_reference && ` (${linkedLegacyOffer.offer_reference})`}
                {" - "}
                {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(linkedLegacyOffer.gross_amount || 0)}
                <Badge className="ml-2 bg-purple-100 text-purple-800" variant="outline">
                  {linkedLegacyOffer.status === "freigegeben" && "Freigegeben"}
                  {linkedLegacyOffer.status === "teilfakturiert" && "Teilfakturiert"}
                  {linkedLegacyOffer.status === "vollständig_fakturiert" && "Vollst. fakturiert"}
                  {linkedLegacyOffer.status === "importiert" && "Importiert"}
                  {linkedLegacyOffer.status === "geprüft" && "Geprüft"}
                  {linkedLegacyOffer.status === "beauftragt" && "Beauftragt"}
                </Badge>
              </div>
              <div className="flex gap-2">
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="shrink-0 border-purple-300 bg-white hover:bg-purple-100 text-purple-800"
                >
                  <Link to={`/legacy-angebote/${linkedLegacyOffer.id}`}>
                    <Eye className="h-4 w-4 mr-2" />
                    Details & Rechnungen
                  </Link>
                </Button>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Option to link legacy offer when no standard offer is linked */}
      {!linkedLegacyOffer && !signedOffer && project.status !== "abgeschlossen" && (
        <Alert className="border-purple-200 bg-purple-50/50">
          <Archive className="h-4 w-4 text-purple-500" />
          <AlertTitle className="text-purple-700">Legacy-Angebot verknüpfen</AlertTitle>
          <AlertDescription className="text-purple-600">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-2">
              <p className="text-sm">
                Sie können ein importiertes Legacy-Angebot mit diesem Projekt verknüpfen, um Abschlagsrechnungen zu erstellen.
              </p>
              <Button
                onClick={() => {
                  loadCustomerLegacyOffers();
                  setShowLegacyDialog(true);
                }}
                variant="outline"
                size="sm"
                className="shrink-0 border-purple-300 bg-white hover:bg-purple-100 text-purple-700"
                data-testid="link-legacy-offer-btn"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Legacy-Angebot zuweisen
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Show linked offer info when project has an accepted offer */}
      {project.status === "angebot_angenommen" && project.accepted_offer_id && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">Verknüpftes Angebot</AlertTitle>
          <AlertDescription className="text-green-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-2">
              <p className="text-sm">
                {(() => {
                  const linkedOffer = projectOffers.find(o => o.id === project.accepted_offer_id);
                  if (linkedOffer) {
                    return (
                      <>
                        <span className="font-medium">{linkedOffer.offer_number}</span>
                        {" - "}
                        {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
                          linkedOffer.display_price ?? linkedOffer.total_price ?? linkedOffer.total_gross ?? linkedOffer.total ?? 0
                        )}
                      </>
                    );
                  }
                  return "Angebot verknüpft";
                })()}
              </p>
              <Button
                onClick={openMarkAcceptedDialog}
                disabled={markingOfferAccepted}
                variant="ghost"
                size="sm"
                className="shrink-0 text-green-700 hover:text-green-900 hover:bg-green-100"
              >
                Ändern
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Kunde */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <User className="h-4 w-4" />
              Kunde
            </CardTitle>
          </CardHeader>
          <CardContent>
            {customer ? (
              <div className="space-y-1">
                <p className="font-medium">{customer.first_name} {customer.last_name}</p>
                <p className="text-sm text-muted-foreground">{customer.email}</p>
                <p className="text-sm text-muted-foreground">{customer.phone}</p>
                <Link to={`/kunden/${customer.id}`} className="text-sm text-primary hover:underline">
                  Details anzeigen →
                </Link>
              </div>
            ) : (
              <p className="text-muted-foreground">Kein Kunde zugeordnet</p>
            )}
          </CardContent>
        </Card>

        {/* Adresse */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Installationsadresse
            </CardTitle>
          </CardHeader>
          <CardContent>
            {customer ? (
              <div className="space-y-1">
                <p>{customer.street}</p>
                <p>{customer.zip} {customer.city}</p>
              </div>
            ) : (
              <p className="text-muted-foreground">Keine Adresse</p>
            )}
          </CardContent>
        </Card>

        {/* Angebot */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Angebot
            </CardTitle>
          </CardHeader>
          <CardContent>
            {signedOffer ? (
              <div className="space-y-1">
                <p className="font-medium">{signedOffer.offer_number}</p>
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Unterschrieben
                </p>
                <p className="text-lg font-bold">{formatCurrency(signedOffer.final_price ?? signedOffer.display_price ?? signedOffer.total_price ?? signedOffer.total_gross ?? signedOffer.total ?? 0)}</p>
                <Link to={`/angebote/${signedOffer.id}`} className="text-sm text-primary hover:underline">
                  Angebot öffnen →
                </Link>
              </div>
            ) : (
              <p className="text-muted-foreground">Kein unterschriebenes Angebot</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dokumente & E-Mail Section */}
      {signedOffer && isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Dokumente & E-Mail-Versand
            </CardTitle>
            <CardDescription>
              Versenden Sie Dokumente an den Kunden per E-Mail
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Angebot versenden */}
              <div className="space-y-3 p-4 border rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <h3 className="font-medium">Angebot</h3>
                </div>
                {signedOffer.offer_sent_at ? (
                  <Badge variant="outline" className="text-green-600">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Versendet am {new Date(signedOffer.offer_sent_at).toLocaleDateString('de-DE')}
                  </Badge>
                ) : (
                  <Badge variant="secondary">Noch nicht versendet</Badge>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={handlePreviewOffer}
                    variant="outline"
                    className="flex-1"
                    data-testid="preview-offer-btn"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Ansehen
                  </Button>
                  <Button
                    onClick={handleSendOffer}
                    disabled={sendingOffer}
                    className="flex-1"
                    variant={signedOffer.offer_sent_at ? "outline" : "default"}
                    data-testid="send-offer-btn"
                  >
                    {sendingOffer ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Senden
                  </Button>
                </div>
              </div>

              {/* Kaufvertrag */}
              <div className="space-y-3 p-4 border rounded-lg">
                <div className="flex items-center gap-2">
                  <FileCheck className="h-5 w-5 text-green-600" />
                  <h3 className="font-medium">Kaufvertrag</h3>
                </div>
                {!signedOffer.contract_generated_at ? (
                  <Badge variant="secondary">Nicht erzeugt</Badge>
                ) : signedOffer.contract_sent_at ? (
                  <Badge variant="outline" className="text-green-600">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Versendet am {new Date(signedOffer.contract_sent_at).toLocaleDateString('de-DE')}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600">
                    Erzeugt, nicht versendet
                  </Badge>
                )}
                {signedOffer.contract_generated_at ? (
                  <div className="flex gap-2">
                    <Button
                      onClick={handlePreviewContract}
                      variant="outline"
                      className="flex-1"
                      data-testid="preview-contract-btn"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Ansehen
                    </Button>
                    <Button
                      onClick={handleSendContract}
                      disabled={sendingContract}
                      className="flex-1"
                      variant={signedOffer.contract_sent_at ? "outline" : "default"}
                      data-testid="send-contract-btn"
                    >
                      {sendingContract ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Senden
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleGenerateContract}
                    disabled={generatingContract}
                    className="w-full"
                    data-testid="generate-contract-btn"
                  >
                    {generatingContract ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Wird erzeugt...
                      </>
                    ) : (
                      <>
                        <FileCheck className="h-4 w-4 mr-2" />
                        Kaufvertrag erzeugen
                      </>
                    )}
                  </Button>
                )}
              </div>

              {/* Auftragsbestätigung */}
              <div className="space-y-3 p-4 border rounded-lg">
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-purple-600" />
                  <h3 className="font-medium">Auftragsbestätigung</h3>
                </div>
                {!project.order_confirmation_generated_at ? (
                  <Badge variant="secondary">Nicht erstellt</Badge>
                ) : project.order_confirmation_sent_at ? (
                  <Badge variant="outline" className="text-green-600">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Versendet am {new Date(project.order_confirmation_sent_at).toLocaleDateString('de-DE')}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600">
                    Erstellt, nicht versendet
                  </Badge>
                )}
                
                {!project.order_confirmation_generated_at ? (
                  <Button
                    onClick={() => handleGenerateOrderConfirmation()}
                    disabled={generatingConfirmation}
                    className="w-full"
                    data-testid="generate-confirmation-btn"
                  >
                    {generatingConfirmation ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ClipboardList className="h-4 w-4 mr-2" />
                    )}
                    Auftragsbestätigung erstellen
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs p-2 bg-muted/40 rounded">
                      <Label htmlFor="ab-show-prices" className="cursor-pointer">Einzelpreise im PDF</Label>
                      <Switch
                        id="ab-show-prices"
                        checked={abShowItemPrices}
                        onCheckedChange={async (checked) => {
                          setAbShowItemPrices(checked);
                          // Persist change → regenerate the cached AB-PDF so „Senden" verschickt das neue Format
                          try {
                            await projectsAPI.generateOrderConfirmation(id, checked);
                            toast.success(checked ? "PDF mit Einzelpreisen aktualisiert" : "PDF ohne Einzelpreise aktualisiert");
                            await loadData();
                          } catch (e) {
                            toast.error("Konnte PDF nicht aktualisieren");
                          }
                        }}
                        data-testid="ab-show-prices-switch"
                      />
                    </div>
                    <Button
                      onClick={() => handleGenerateOrderConfirmation(abShowItemPrices)}
                      disabled={generatingConfirmation}
                      variant="outline"
                      size="sm"
                      className="w-full"
                      data-testid="regenerate-confirmation-btn"
                    >
                      {generatingConfirmation ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Neu generieren (mit aktuellen Texten)
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        onClick={handlePreviewOrderConfirmation}
                        variant="outline"
                        className="flex-1"
                        data-testid="preview-confirmation-btn"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Ansehen
                      </Button>
                      <Button
                        onClick={handleSendOrderConfirmation}
                        disabled={sendingConfirmation}
                        className="flex-1"
                        variant={project.order_confirmation_sent_at ? "outline" : "default"}
                        data-testid="send-confirmation-btn"
                      >
                        {sendingConfirmation ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4 mr-2" />
                        )}
                        Senden
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Anlagentyp & Meldungen */}
      {isAdmin && (
        <Card data-testid="registrations-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle>Anlagentyp & Behörden-Meldungen</CardTitle>
                <CardDescription>
                  Trage die Registriernummer und das Datum ein – eine Aufgabe gilt erst als erledigt, wenn beides ausgefüllt ist.
                </CardDescription>
              </div>
              {registrations.system_type && (
                <ProjectStatusLamps
                  project={{ ...registrations, order_confirmation_sent_at: project?.order_confirmation_sent_at || "preview" }}
                  size="large"
                  showLabel
                />
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Anlagentyp */}
            <div className="max-w-xs">
              <Label htmlFor="system-type">Anlagentyp</Label>
              <Select
                value={registrations.system_type || "__none__"}
                onValueChange={(v) => setRegistrations({ ...registrations, system_type: v === "__none__" ? "" : v })}
              >
                <SelectTrigger data-testid="system-type-select">
                  <SelectValue placeholder="Bitte wählen…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">– Bitte wählen –</SelectItem>
                  <SelectItem value="pv">Photovoltaik</SelectItem>
                  <SelectItem value="heatpump">Wärmepumpe</SelectItem>
                  <SelectItem value="climate">Klimaanlage</SelectItem>
                  <SelectItem value="other">Sonstiges</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* PV: 3 Meldungen */}
            {registrations.system_type === "pv" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t">
                {[
                  { key: "einspeiseanfrage", label: "Einspeiseanfrage" },
                  { key: "marktstammdaten", label: "Marktstammdatenregister" },
                  { key: "fertigmeldung", label: "Fertigmeldung" },
                ].map(({ key, label }) => {
                  const numKey = `${key}_number`;
                  const dateKey = `${key}_date`;
                  const done = Boolean(registrations[numKey] && registrations[dateKey]);
                  return (
                    <div key={key} className="space-y-2 p-3 rounded-lg border bg-card">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">{label}</Label>
                        <span
                          className={`h-3 w-3 rounded-full ${done ? "bg-emerald-500" : "bg-red-500"} ring-2 ring-offset-1 ring-offset-background ${done ? "ring-emerald-300" : "ring-red-300"}`}
                          aria-label={done ? "erledigt" : "offen"}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`${key}-num`} className="text-xs text-muted-foreground">Nummer</Label>
                        <Input
                          id={`${key}-num`}
                          value={registrations[numKey]}
                          onChange={(e) => setRegistrations({ ...registrations, [numKey]: e.target.value })}
                          placeholder="z. B. EA-123456"
                          data-testid={`${key}-number-input`}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`${key}-date`} className="text-xs text-muted-foreground">Datum</Label>
                        <Input
                          id={`${key}-date`}
                          type="date"
                          value={registrations[dateKey]}
                          onChange={(e) => setRegistrations({ ...registrations, [dateKey]: e.target.value })}
                          data-testid={`${key}-date-input`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* WP / Klima: §14a */}
            {(registrations.system_type === "heatpump" || registrations.system_type === "climate") && (
              <div className="pt-2 border-t">
                <div className="max-w-md space-y-2 p-3 rounded-lg border bg-card">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">§14a EnWG-Meldung</Label>
                    <span
                      className={`h-3 w-3 rounded-full ${registrations.paragraph14a_number && registrations.paragraph14a_date ? "bg-emerald-500 ring-emerald-300" : "bg-red-500 ring-red-300"} ring-2 ring-offset-1 ring-offset-background`}
                    />
                  </div>
                  <div>
                    <Label htmlFor="p14a-num" className="text-xs text-muted-foreground">Nummer</Label>
                    <Input
                      id="p14a-num"
                      value={registrations.paragraph14a_number}
                      onChange={(e) => setRegistrations({ ...registrations, paragraph14a_number: e.target.value })}
                      placeholder="z. B. NB-14a-2026-0815"
                      data-testid="paragraph14a-number-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="p14a-date" className="text-xs text-muted-foreground">Datum</Label>
                    <Input
                      id="p14a-date"
                      type="date"
                      value={registrations.paragraph14a_date}
                      onChange={(e) => setRegistrations({ ...registrations, paragraph14a_date: e.target.value })}
                      data-testid="paragraph14a-date-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {registrations.system_type === "other" && (
              <p className="text-sm text-muted-foreground italic pt-2 border-t">
                Für „Sonstiges" sind keine Pflichtmeldungen hinterlegt.
              </p>
            )}

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSaveRegistrations}
                disabled={savingRegistrations}
                data-testid="save-registrations-btn"
              >
                {savingRegistrations ? "Speichern…" : "Speichern"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rechnungen Section */}
      {signedOffer && isAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Rechnungen
                </CardTitle>
                <CardDescription>
                  Erstellen und versenden Sie Rechnungen für dieses Projekt
                </CardDescription>
              </div>
              <Button 
                onClick={() => {
                  const options = getAvailableInvoiceOptions();
                  if (options.canCreateAbschlag && options.availablePartials.length > 0) {
                    setNewInvoiceType("abschlagsrechnung");
                    const slot = options.availablePartials[0];
                    setNewInvoicePartial(slot.toString());
                    // Default-Prozent: 60 für Abschlag 1, 30 für Abschlag 2 (bzw. 100 bei Vorkasse)
                    const defaultPct = signedOffer?.payment_terms === "prepayment" ? 100 : (slot === 1 ? 60 : 30);
                    setNewInvoiceCustomPct(defaultPct.toString());
                  } else if (options.canCreateSchluss) {
                    setNewInvoiceType("schlussrechnung");
                    setNewInvoiceCustomPct("100");
                    if (options.isSchlussauflistung) {
                      setNewInvoiceForceZeroBalance(true);
                    }
                  }
                  setShowInvoiceDialog(true);
                }}
                disabled={!getAvailableInvoiceOptions().canCreateAbschlag && !getAvailableInvoiceOptions().canCreateSchluss}
                data-testid="create-invoice-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Rechnung erstellen
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Show message when order confirmation is required */}
            {getAvailableInvoiceOptions().requiresOrderConfirmation && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                Bitte erstellen Sie zuerst eine Auftragsbestätigung, bevor Sie Rechnungen erstellen können.
              </div>
            )}
            
            {/* Show message when all invoices are created */}
            {!getAvailableInvoiceOptions().requiresOrderConfirmation && !getAvailableInvoiceOptions().canCreateAbschlag && !getAvailableInvoiceOptions().canCreateSchluss && invoices.length > 0 && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                Alle Rechnungen für dieses Projekt wurden erstellt.
              </div>
            )}
            
            {invoices.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Receipt className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Noch keine Rechnungen erstellt</p>
                <p className="text-sm">Klicken Sie auf &quot;Rechnung erstellen&quot; um eine neue Rechnung anzulegen</p>
              </div>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${
                        invoice.payment_status === "paid" ? "bg-green-100" :
                        invoice.status === "entwurf" ? "bg-amber-100" :
                        invoice.status === "final" ? "bg-blue-100" : "bg-red-100"
                      }`}>
                        <Receipt className={`h-5 w-5 ${
                          invoice.payment_status === "paid" ? "text-green-600" :
                          invoice.status === "entwurf" ? "text-amber-600" :
                          invoice.status === "final" ? "text-blue-600" : "text-red-600"
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">
                            {invoice.invoice_number || "Entwurf"}
                          </span>
                          {getInvoiceStatusBadge(invoice)}
                          {getPaymentStatusBadge(invoice)}
                          {invoice.sent_at && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              <Send className="h-3 w-3 mr-1" />
                              Versendet
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {getInvoiceTypeName(invoice.invoice_type)}
                          {invoice.partial_number ? ` ${invoice.partial_number}` : ''}
                          {invoice.partial_percentage ? ` (${invoice.partial_percentage}%)` : ''}
                        </p>
                        <p className="text-lg font-semibold">
                          {formatCurrency(invoice.gross_total)}
                          {invoice.skonto_amount ? (
                            <span className="text-sm font-normal text-muted-foreground ml-2">
                              (inkl. {invoice.skonto_percent}% Skonto)
                            </span>
                          ) : null}
                        </p>
                        {invoice.paid_at && (
                          <p className="text-xs text-green-600">
                            Bezahlt am {new Date(invoice.paid_at).toLocaleDateString('de-DE')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Preview button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePreviewInvoice(invoice)}
                        data-testid={`preview-invoice-${invoice.id}`}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {invoice.status === "entwurf" ? "Vorschau" : "PDF"}
                      </Button>
                      
                      {/* Finalize button (only for drafts) */}
                      {invoice.status === "entwurf" && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleFinalizeInvoice(invoice)}
                          disabled={finalizingInvoice === invoice.id}
                          data-testid={`finalize-invoice-${invoice.id}`}
                        >
                          {finalizingInvoice === invoice.id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <FileCheck className="h-4 w-4 mr-2" />
                          )}
                          Finalisieren
                        </Button>
                      )}
                      
                      {/* Send button (only for finalized) */}
                      {invoice.status === "final" && invoice.invoice_type !== "stornorechnung" && (
                        <Button
                          variant={invoice.sent_at ? "outline" : "default"}
                          size="sm"
                          onClick={() => handleSendInvoice(invoice)}
                          disabled={sendingInvoice === invoice.id}
                          data-testid={`send-invoice-${invoice.id}`}
                        >
                          {sendingInvoice === invoice.id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4 mr-2" />
                          )}
                          {invoice.sent_at ? "Erneut senden" : "Senden"}
                        </Button>
                      )}
                      
                      {/* Mark as paid button (only for finalized, not stornorechnung, not already paid) */}
                      {invoice.status === "final" && invoice.invoice_type !== "stornorechnung" && invoice.payment_status !== "paid" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleMarkAsPaid(invoice)}
                          disabled={markingAsPaid === invoice.id}
                          className="border-green-300 text-green-700 hover:bg-green-50"
                          data-testid={`mark-paid-${invoice.id}`}
                        >
                          {markingAsPaid === invoice.id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2" />
                          )}
                          Bezahlt
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Projektverlauf
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <div>
                <p className="font-medium">Projekt erstellt</p>
                <p className="text-sm text-muted-foreground">
                  {project.created_at ? new Date(project.created_at).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : "-"}
                </p>
              </div>
            </div>
            
            {signedOffer?.signed_at && (
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <div>
                  <p className="font-medium">Angebot unterschrieben</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(signedOffer.signed_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            )}
            
            {signedOffer?.contract_generated_at && (
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <div>
                  <p className="font-medium">Kaufvertrag erzeugt</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(signedOffer.contract_generated_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            )}
            
            {project.order_confirmation_generated_at && (
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <div>
                  <p className="font-medium">Auftragsbestätigung erstellt</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(project.order_confirmation_generated_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoice Creation Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Neue Rechnung erstellen</DialogTitle>
            <DialogDescription>
              Erstellen Sie eine neue Rechnung für das Angebot {signedOffer?.offer_number}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Payment terms info */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm">
              <strong>Zahlungsbedingung:</strong>{" "}
              {signedOffer?.payment_terms === "prepayment" 
                ? "Vorkasse (100% mit 5% Skonto)" 
                : "Standard (60% / 30% / 10%)"}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="invoice-type">Rechnungstyp</Label>
              <Select
                value={newInvoiceType}
                onValueChange={(v) => {
                  setNewInvoiceType(v);
                  if (v === "schlussrechnung") {
                    // Default 100% (voller Auftragswert – Anzahlungen werden im Backend automatisch abgezogen)
                    setNewInvoiceCustomPct("100");
                    // Existiert bereits eine Schluss → dieses Dokument wird eine Schlussauflistung
                    // mit hart 0 € Zahlbetrag. Checkbox automatisch vorbelegen.
                    if (getAvailableInvoiceOptions().isSchlussauflistung) {
                      setNewInvoiceForceZeroBalance(true);
                    }
                  } else if (v === "abschlagsrechnung") {
                    // Default 60/30 je nach Slot
                    const num = parseInt(newInvoicePartial) || 1;
                    const defaultPct = signedOffer?.payment_terms === "prepayment" ? 100 : (num === 1 ? 60 : 30);
                    setNewInvoiceCustomPct(defaultPct.toString());
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Typ auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableInvoiceOptions().canCreateAbschlag && (
                    <SelectItem value="abschlagsrechnung">Abschlagsrechnung</SelectItem>
                  )}
                  {getAvailableInvoiceOptions().canCreateSchluss && (
                    <SelectItem value="schlussrechnung">
                      {getAvailableInvoiceOptions().isSchlussauflistung
                        ? "Schlussauflistung (0 €, §35a EStG-Nachweis)"
                        : "Schlussrechnung"}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            {/* Schlussrechnung: Prozent + Anzahlungs-Vorschau */}
            {newInvoiceType === "schlussrechnung" && (
              <div className="space-y-2">
                <Label htmlFor="schluss-pct">Prozentsatz der Schlussrechnung</Label>
                <div className="relative">
                  <Input
                    id="schluss-pct"
                    type="number"
                    min="0.01"
                    max="100"
                    step="0.01"
                    value={newInvoiceCustomPct}
                    onChange={(e) => setNewInvoiceCustomPct(e.target.value)}
                    className="pr-10"
                    data-testid="invoice-schluss-pct-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                </div>
                {(() => {
                  const pct = parseFloat(newInvoiceCustomPct);
                  const offerGross = parseFloat(signedOffer?.display_price ?? signedOffer?.total_price ?? signedOffer?.total ?? 0);
                  const billed = (!isNaN(pct) && offerGross > 0) ? (offerGross * pct / 100) : 0;
                  const anzahlungen = invoices
                    .filter(i => i.invoice_type === "abschlagsrechnung" && i.status === "final")
                    .reduce((s, i) => s + (parseFloat(i.gross_total) || 0), 0);
                  const restRaw = billed - anzahlungen;
                  const rest = newInvoiceForceZeroBalance ? 0 : restRaw;
                  const fmt = (v) => v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
                  return (
                    <div className="mt-2 p-3 rounded-lg border bg-muted/30 space-y-1 text-sm" data-testid="schluss-preview">
                      <div className="flex justify-between"><span className="text-muted-foreground">Rechnungssumme ({isNaN(pct) ? "-" : pct}% von {fmt(offerGross)})</span><span className="tabular-nums font-medium">{fmt(billed)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">./. bereits geleistete Anzahlungen</span><span className="tabular-nums text-red-600">-{fmt(anzahlungen)}</span></div>
                      <div className="flex justify-between border-t pt-1 mt-1"><span className="font-medium">Restbetrag (Zahlbetrag)</span><span className={`tabular-nums font-bold ${rest < 0 ? "text-red-600" : ""}`}>{fmt(rest)}</span></div>
                      {!newInvoiceForceZeroBalance && restRaw < 0 && (
                        <p className="text-xs text-red-600 mt-1">⚠ Überzahlung – die Anzahlungen übersteigen die Schlussrechnungssumme.</p>
                      )}
                      {newInvoiceForceZeroBalance && (
                        <p className="text-xs text-emerald-700 mt-1">Nullbetrag-Modus aktiv: Zahlbetrag wird auf 0,00 € gesetzt und der §35a-EStG-Lohnkostenhinweis erscheint im PDF.</p>
                      )}
                    </div>
                  );
                })()}

                {/* „Nullbetrag"-Schlussrechnung (§35a EStG Lohnkostennachweis) */}
                <div className="mt-3 flex items-start gap-2 p-3 rounded-lg border bg-amber-50 border-amber-200">
                  <input
                    type="checkbox"
                    id="force-zero-balance"
                    className="mt-1 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                    checked={newInvoiceForceZeroBalance}
                    onChange={(e) => setNewInvoiceForceZeroBalance(e.target.checked)}
                    data-testid="invoice-force-zero-balance-checkbox"
                  />
                  <label htmlFor="force-zero-balance" className="text-sm cursor-pointer">
                    <span className="font-medium">
                      {getAvailableInvoiceOptions().isSchlussauflistung
                        ? "Als Schlussauflistung mit 0 € erzeugen"
                        : "Restbetrag auf 0 € setzen"}
                    </span>{" "}
                    <span className="text-muted-foreground">
                      {getAvailableInvoiceOptions().isSchlussauflistung ? (
                        <>
                          – Für dieses Angebot existiert bereits eine Schlussrechnung. Das neue Dokument
                          wird automatisch als <strong>Schlussauflistung</strong> gekennzeichnet, listet
                          <strong> alle bisherigen Rechnungen</strong> (Abschläge + Schluss) auf und weist den
                          <strong> Lohnkostenanteil nach §35a EStG</strong> aus. Zahlbetrag: 0,00 €.
                        </>
                      ) : (
                        <>
                          – Kunde hat bereits alle Abschläge bezahlt. Die Schlussrechnung dokumentiert die Zahlungen und weist zusätzlich den <strong>Lohnkostenanteil nach §35a EStG</strong> aus.
                        </>
                      )}
                    </span>
                  </label>
                </div>
              </div>
            )}
            
            {newInvoiceType === "abschlagsrechnung" && (
              <div className="space-y-2">
                <Label htmlFor="partial-number">Abschlagsnummer</Label>
                <Select 
                  value={newInvoicePartial} 
                  onValueChange={(v) => {
                    setNewInvoicePartial(v);
                    const num = parseInt(v);
                    const defaultPct = signedOffer?.payment_terms === "prepayment" ? 100 : (num === 1 ? 60 : 30);
                    setNewInvoiceCustomPct(defaultPct.toString());
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Nummer auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableInvoiceOptions().availablePartials.map(num => {
                      const pct = signedOffer?.payment_terms === "prepayment" ? 100 : (num === 1 ? 60 : 30);
                      return (
                        <SelectItem key={num} value={num.toString()}>
                          Abschlag {num} (Standard: {pct}%)
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                
                {/* Individueller Prozentsatz */}
                <div className="pt-2">
                  <Label htmlFor="custom-pct">Prozentsatz dieser Abschlagsrechnung</Label>
                  <div className="relative">
                    <Input
                      id="custom-pct"
                      type="number"
                      min="0.01"
                      max="100"
                      step="0.01"
                      value={newInvoiceCustomPct}
                      onChange={(e) => setNewInvoiceCustomPct(e.target.value)}
                      className="pr-10"
                      data-testid="invoice-custom-pct-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                  </div>
                  {(() => {
                    const pct = parseFloat(newInvoiceCustomPct);
                    const offerGross = parseFloat(signedOffer?.display_price ?? signedOffer?.total_price ?? 0);
                    const calcAmount = (!isNaN(pct) && pct > 0 && offerGross > 0) ? (offerGross * pct / 100) : null;
                    const otherSum = invoices
                      .filter(i => i.invoice_type === "abschlagsrechnung" && i.status !== "storno" && i.partial_number !== parseInt(newInvoicePartial))
                      .reduce((s, i) => s + (parseFloat(i.partial_percentage) || 0), 0);
                    const total = otherSum + (isNaN(pct) ? 0 : pct);
                    const exceeds = total > 100.0001;
                    return (
                      <div className="mt-1 space-y-0.5">
                        {calcAmount !== null && (
                          <p className="text-xs text-muted-foreground">
                            Betrag (brutto): <span className="font-medium tabular-nums">{calcAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span>
                          </p>
                        )}
                        <p className={`text-xs ${exceeds ? 'text-red-600 font-medium' : 'text-muted-foreground'}`} data-testid="invoice-pct-sum-info">
                          Summe aller Abschlagsprozente: {total.toFixed(2)}%{exceeds ? ' – übersteigt 100%!' : ` (Rest: ${(100 - total).toFixed(2)}%)`}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
            
            {/* MwSt-Toggle NUR für Non-PV-Angebote (Freitext / WP / Legacy) */}
            {signedOffer && signedOffer._doc_type && signedOffer._doc_type !== "offer" && (
              <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg" data-testid="invoice-vat-toggle-wrapper">
                <div>
                  <Label htmlFor="invoice-include-vat-switch" className="cursor-pointer font-medium">MwSt in Rechnung ausweisen</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    An: nutzt den in der Firmen-CI hinterlegten MwSt-Satz. Aus: reine Nettorechnung ohne MwSt-Ausweis.
                  </p>
                </div>
                <Switch
                  id="invoice-include-vat-switch"
                  checked={newInvoiceIncludeVat}
                  onCheckedChange={setNewInvoiceIncludeVat}
                  data-testid="invoice-include-vat-switch"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="notes">Notizen (optional)</Label>
              <Textarea
                id="notes"
                value={newInvoiceNotes}
                onChange={(e) => setNewInvoiceNotes(e.target.value)}
                placeholder="Interne Notizen zur Rechnung..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleCreateInvoice} 
              disabled={creatingInvoice || (!getAvailableInvoiceOptions().canCreateAbschlag && !getAvailableInvoiceOptions().canCreateSchluss)}
            >
              {creatingInvoice ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Rechnung erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog for marking offer as accepted */}
      <Dialog open={showMarkAcceptedDialog} onOpenChange={setShowMarkAcceptedDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {project?.status === "angebot_angenommen" 
                ? "Angebot zuweisen / ändern"
                : "Angebot als angenommen markieren"
              }
            </DialogTitle>
            <DialogDescription>
              {project?.status === "angebot_angenommen" 
                ? "Wählen Sie das Angebot aus, das mit diesem Projekt verknüpft werden soll."
                : "Wählen Sie das Angebot aus, das der Kunde angenommen hat. Das Projekt wird dann auf \"Angebot angenommen\" gesetzt und Sie können mit der weiteren Bearbeitung fortfahren."
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="offer-select">Angebot auswählen *</Label>
              <Select value={selectedOfferId} onValueChange={setSelectedOfferId}>
                <SelectTrigger id="offer-select" data-testid="offer-select">
                  <SelectValue placeholder="Bitte Angebot auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {projectOffers.map((offer) => {
                    // Different offer types use different price field names:
                    //   Native PV → display_price / total_price
                    //   Free Offer → total (brutto)
                    //   WP Offer + Legacy Offer → total_gross
                    const offerPrice = (
                      offer.display_price ??
                      offer.total_price ??
                      offer.total_gross ??
                      offer.total ??
                      0
                    );
                    return (
                      <SelectItem key={offer.id} value={offer.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{offer.offer_number}</span>
                          <span className="text-muted-foreground">
                            - {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(offerPrice)}
                          </span>
                          {offer.status === "angenommen" && (
                            <Badge variant="default" className="ml-2 bg-green-500">Angenommen</Badge>
                          )}
                          {offer.signature && (
                            <Badge variant="outline" className="ml-2">Signiert</Badge>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {projectOffers.length === 0 && (
                <p className="text-sm text-destructive">
                  Keine Angebote für dieses Projekt vorhanden.
                </p>
              )}
            </div>
            
            {selectedOfferId && (
              <div className="rounded-lg border p-3 bg-muted/50">
                <p className="text-sm text-muted-foreground">
                  Das ausgewählte Angebot wird als "angenommen" markiert und mit dem 
                  Projekt verknüpft. Sie können danach Kaufvertrag, Auftragsbestätigung 
                  und Rechnungen erstellen.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarkAcceptedDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleConfirmMarkOfferAccepted}
              disabled={markingOfferAccepted || !selectedOfferId}
              data-testid="confirm-mark-accepted-btn"
            >
              {markingOfferAccepted ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird markiert...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Bestätigen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog for linking legacy offer */}
      <Dialog open={showLegacyDialog} onOpenChange={setShowLegacyDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Legacy-Angebot verknüpfen</DialogTitle>
            <DialogDescription>
              Wählen Sie ein importiertes Legacy-Angebot aus, das mit diesem Projekt verknüpft werden soll.
              Sie können dann Abschlagsrechnungen aus dem Legacy-Angebot erstellen.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {customerLegacyOffers.length === 0 ? (
              <div className="text-center py-6">
                <Archive className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">Keine Legacy-Angebote für diesen Kunden gefunden.</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Gehen Sie zum Kundenprofil → Tab "Legacy" → "Dokument importieren"
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="legacy-select">Legacy-Angebot auswählen *</Label>
                <Select value={selectedLegacyOfferId} onValueChange={setSelectedLegacyOfferId}>
                  <SelectTrigger id="legacy-select" data-testid="legacy-offer-select">
                    <SelectValue placeholder="Bitte Legacy-Angebot auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {customerLegacyOffers.map((legacy) => (
                      <SelectItem key={legacy.id} value={legacy.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{legacy.title || "Ohne Titel"}</span>
                          {legacy.offer_reference && (
                            <span className="text-muted-foreground text-xs">({legacy.offer_reference})</span>
                          )}
                          <span className="text-muted-foreground">
                            - {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(legacy.gross_amount || 0)}
                          </span>
                          <Badge 
                            variant="outline" 
                            className={
                              legacy.status === "freigegeben" ? "bg-green-100 text-green-800" :
                              legacy.status === "teilfakturiert" ? "bg-amber-100 text-amber-800" :
                              "bg-gray-100 text-gray-800"
                            }
                          >
                            {legacy.status === "freigegeben" && "Freigegeben"}
                            {legacy.status === "teilfakturiert" && "Teilfakturiert"}
                            {legacy.status === "importiert" && "Importiert"}
                            {legacy.status === "geprüft" && "Geprüft"}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLegacyDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleAssignLegacyOffer}
              disabled={assigningLegacy || !selectedLegacyOfferId}
              data-testid="confirm-link-legacy-btn"
            >
              {assigningLegacy ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird verknüpft...
                </>
              ) : (
                <>
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Verknüpfen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
